import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import Footer from '../components/ui/Footer';

function getSiteUrl(): string {
  if (typeof window === 'undefined') return 'https://www.ms4all.eu/';
  const hostname = window.location.hostname.toLowerCase();
  return hostname.endsWith('.fr') || hostname.includes('ms4all.fr')
    ? 'https://www.ms4all.fr/'
    : 'https://www.ms4all.eu/';
}

const linkClass = 'text-[#006AB5] underline hover:text-[#005A99]';

function LegalNotice() {
  const { t } = useTranslation();
  const siteUrl = getSiteUrl();
  const gtcUrl = `${siteUrl}documents/251201GTCms4all.pdf`;
  const gtcLabel = gtcUrl.replace(/^https?:\/\//, '');

  return (
    <>
      <Helmet>
        <title>{t('legalNotice.metadata.title')}</title>
        <meta
          name="description"
          content={t('legalNotice.metadata.description')}
        />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Header */}
          <div className="mx-auto flex flex-col max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
              {t('legalNotice.title')}
            </h1>
          </div>

        {/* Content */}
        <main className="mx-auto flex flex-col gap-8 max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          {/* Publisher */}
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t('legalNotice.sections.publisher.title')}
            </h2>
            <p>{t('legalNotice.sections.publisher.intro', { siteUrl })}</p>
            <address className="flex flex-col gap-1 not-italic">
              <p className="font-semibold">
                {t('legalNotice.sections.publisher.company')}
              </p>
              <p>{t('legalNotice.sections.publisher.legalForm')}</p>
              <p>{t('legalNotice.sections.publisher.siret')}</p>
              <p>
                {t('legalNotice.sections.publisher.addressLabel')}{' '}
                {t('legalNotice.sections.publisher.address')}
              </p>
              <p>{t('legalNotice.sections.publisher.vat')}</p>
              <p>{t('legalNotice.sections.publisher.ape')}</p>
            </address>
          </section>

          {/* Director */}
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t('legalNotice.sections.director.title')}
            </h2>
            <p>{t('legalNotice.sections.director.body')}</p>
          </section>

          {/* Contact */}
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t('legalNotice.sections.contact.title')}
            </h2>
            <p>
              {t('legalNotice.sections.contact.emailLabel')} contact@ms4all.eu
            </p>
            <p>
              {t('legalNotice.sections.contact.phoneLabel')} +33 (0)6 48 00 29 48
            </p>
          </section>

          {/* Hosting */}
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t('legalNotice.sections.hosting.title')}
            </h2>
            <p>
              {t('legalNotice.sections.hosting.nameLabel')}{' '}
              {t('legalNotice.sections.hosting.nameValue')}
            </p>
            <p>
              {t('legalNotice.sections.hosting.addressLabel')}{' '}
              {t('legalNotice.sections.hosting.addressValue')}
            </p>
            <p>
              {t('legalNotice.sections.hosting.contactLabel')}{' '}
              {t('legalNotice.sections.hosting.contactValue')}
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t('legalNotice.sections.ip.title')}
            </h2>
            <p>{t('legalNotice.sections.ip.p1')}</p>
            <p>{t('legalNotice.sections.ip.p2')}</p>
          </section>

          {/* Privacy */}
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t('legalNotice.sections.privacy.title')}
            </h2>
            <p>{t('legalNotice.sections.privacy.p1')}</p>
            <p>{t('legalNotice.sections.privacy.p2')}</p>
            <p>
              {t('legalNotice.sections.privacy.p3Before')}{' '}
              <a
                href={gtcUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                {gtcLabel}
              </a>
              {t('legalNotice.sections.privacy.p3After')}
            </p>
            <p>{t('legalNotice.sections.privacy.p4')}</p>
            <h3 className="mt-2 text-lg font-semibold text-gray-900">
              {t('legalNotice.sections.privacy.rightsTitle')}
            </h3>
            <p>{t('legalNotice.sections.privacy.p5')}</p>
            <p>
              {t('legalNotice.sections.privacy.exerciseLabel')}{' '}
              administration@ms4all.eu
            </p>
            <p>{t('legalNotice.sections.privacy.idNote')}</p>
            <p>
              <Trans
                i18nKey="legalNotice.sections.privacy.cnil"
                components={{
                  a: (
                    <a
                      href="https://www.cnil.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    />
                  ),
                }}
              />
            </p>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default LegalNotice;
